import './Popup.scss';
import cn from 'classnames';
import { InlineWidget } from 'react-calendly';
import { useRef, useState } from 'react';
import { isValidPhoneNumber } from 'libphonenumber-js';

const FORM_URL = 'https://hooks.zapier.com/hooks/catch/12344184/u858ktx/';

const Popup = ({ onClose }) => {
  const [data, setData] = useState({});
  const formRef = useRef(null);
  const [isSending, setSending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isCheckboxActive, setCheckboxActive] = useState(false);

  const handleChangeField = (name, value) => {
    const newData = { ...data };
    newData[name] = value;
    setData(newData);
  };

  const handleSubmit = e => {
    e.preventDefault();
    if ((isCheckboxActive && !data.phone?.trim()) || (!isCheckboxActive && data.phone?.trim()) || !isValidPhone(data.phone))
      return;

    setSending(true);
    const organization_name = formRef.current.querySelector('#organization_name').value;
    const phone = formRef.current.querySelector('#phone').value;
    const email = formRef.current.querySelector('#email').value;
    const contact_name = formRef.current.querySelector('#contact_name').value || '';
    const preferred_positions = formRef.current.querySelector('#preferred_positions').value || '';
    const start_date = formRef.current.querySelector('#start_date').value || '';
    const additional_comments = formRef.current.querySelector('#additional_comments').value || '';

    const values = {
      organization_name,
      contact_name,
      email,
      phone,
      preferred_positions,
      start_date,
      additional_comments,
      sender_type: 'client',
    };

    fetch(`${FORM_URL}`, {
      method: 'POST',
      mode: 'no-cors',
      body: JSON.stringify(values),
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(() => {
      setIsSuccess(true);
      setSending(false);
    });
  };

  const isValidEmail = email => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const isValidPhone = phone => !phone?.trim() || isValidPhoneNumber(phone.trim(), 'US');

  return (
    <>
      <span className='popupBack' />
      <div className='popup'>
        <span className='popupClose' onClick={onClose}>
          <svg width='36' height='36' viewBox='0 0 36 36' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M23 13L13 23M13 13L23 23'
              stroke='#71717A'
              stroke-width='1.5'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
          </svg>
        </span>
        {isSuccess && (
          <div className='mainFormSuccess'>
            <svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120' fill='none'>
              <path
                d='M45 60L55 70L75 50M110 60C110 87.6142 87.6142 110 60 110C32.3858 110 10 87.6142 10 60C10 32.3858 32.3858 10 60 10C87.6142 10 110 32.3858 110 60Z'
                stroke='#14A852'
                stroke-width='5'
                stroke-linecap='round'
                stroke-linejoin='round'
              />
            </svg>
            <span className='mainFormSuccessTitle'>Success!</span>
            <span className='mainFormSuccessText'>We’ve received your request and will contact you within 1 business day.</span>
          </div>
        )}
        <span className='popupTitle'>Send Hiring Request</span>
        <div className='mainFormFields' ref={formRef}>
          <div className='mainFormField'>
            <label className='mainFormLabel'>
              Organization Name <span className='mainFormRequiredMark'>*</span>
            </label>
            <input
              className='mainFormInput'
              value={data.organization_name}
              id='organization_name'
              onChange={e => handleChangeField('organization_name', e.target.value)}
            />
          </div>
          <div className='mainFormField'>
            <label className='mainFormLabel'>
              Contact Name <span className='mainFormRequiredMark'>*</span>
            </label>
            <input
              className='mainFormInput'
              value={data.contact_name}
              id='contact_name'
              onChange={e => handleChangeField('contact_name', e.target.value)}
            />
          </div>
          <div className='mainFormField'>
            <label className='mainFormLabel'>
              Email Address <span className='mainFormRequiredMark'>*</span>
            </label>
            <input
              className='mainFormInput'
              value={data.email}
              id='email'
              onChange={e => handleChangeField('email', e.target.value)}
            />
          </div>
          <div className='mainFormField'>
            <label className='mainFormLabel'>
              Phone Number{isCheckboxActive && <span className='mainFormRequiredMark'> *</span>}
            </label>
            <input
              className='mainFormInput'
              value={data.phone}
              id='phone'
              onChange={e => handleChangeField('phone', e.target.value)}
            />
          </div>
          <div className='mainFormField'>
            <label className='mainFormLabel'>Preferred Positions</label>
            <input
              className='mainFormInput'
              value={data.preferred_positions}
              id='preferred_positions'
              onChange={e => handleChangeField('preferred_positions', e.target.value)}
            />
          </div>
          <div className='mainFormField'>
            <label className='mainFormLabel'>Start Date</label>
            <input
              className='mainFormInput'
              value={data.start_date}
              id='start_date'
              onChange={e => handleChangeField('start_date', e.target.value)}
            />
          </div>

          <div className='mainFormTextarea'>
            <label className='mainFormLabel'>Additional Comments</label>
            <textarea
              className='mainFormComment'
              value={data.additional_comments}
              row={3}
              id='additional_comments'
              onChange={e => handleChangeField('additional_comments', e.target.value)}
            ></textarea>
          </div>
        </div>
        <div
          className={cn('mainFormAgreeCheck', !isCheckboxActive && data.phone?.trim() && 'mainFormAgreeCheckError')}
        >
          <label>
            <input
              type='checkbox'
              checked={isCheckboxActive}
              onChange={e => setCheckboxActive(e.target.checked)}
            />
            <span>
              By checking this box and providing my phone number, I agree to receive SMS/text messages from American Consultants
              regarding job opportunities, interview scheduling, application updates, onboarding information, and service-related
              notifications. Message frequency varies (up to 10 msgs/month). Msg & data rates may apply. Reply STOP to opt out or
              HELP for help. Mobile information and consent to receive SMS will not be shared with third parties or affiliates for
              marketing or promotional purposes. See our{' '}
              <a
                href='https://app.termly.io/policy-viewer/policy.html?policyUUID=d94582df-8db3-4369-9a8a-e1e1f088c58d'
                target='_blank'
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href='https://app.termly.io/policy-viewer/policy.html?policyUUID=c39976da-bc93-427b-a511-25e33d082944'
                target='_blank'
              >
                Terms & Conditions
              </a>{' '}
              for details.
            </span>
          </label>
        </div>
        <div
          className={cn(
            'mainFormButton',
            (!data.organization_name?.trim() ||
              !data.email?.trim() ||
              !isValidEmail(data.email) ||
              (isCheckboxActive && !data.phone?.trim()) ||
              (!isCheckboxActive && data.phone?.trim()) ||
              !isValidPhone(data.phone) ||
              !data.contact_name?.trim() ||
              isSending) &&
              'mainFormButtonDisabled',
          )}
          onClick={e => handleSubmit(e)}
        >
          Find Your Next Hire
        </div>
      </div>
    </>
  );
};

export default Popup;
